import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { handleGetMaximum, isMaxReached } from 'state/loads/effects';
import { actions as warningActions } from 'state/warnings';
import { actions as errorActions } from 'state/errors';
import {
  addDays,
  getHoursDifferenceByTimes,
} from 'utils/dateAndTimes/methods/methods';

import api from 'api';

import {
  actions as orderActions,
  actionsByType,
  sagasByType,
  selectors as orderSelectors,
} from 'state/order';
import { selectors as dorRangeSelectors } from 'state/direct-order-recurring/range';
import { selectors as dorDropOffSelectors } from 'state/direct-order-recurring/drop-off';

import {
  constants as dateAndTimeConstants,
  formatters as dateAndTimeFormatters,
  methods as dateAndTimeMethods,
  validators as dateAndTimeValidators,
} from 'utils/dateAndTimes';

import {
  actions as shipmentsActions,
  actions as shipmentsAction,
  selectors as shipmentsSelectors,
  types,
} from 'state/shipments';
import utils from 'state/shipments/utils';
import { isEmpty } from 'utils/types';
import { constants as orderConstants } from 'utils/order';
import keyGenerator from 'utils/keys';
import { methods as errorsMethods } from 'utils/errors';

function* handleChangeExtras({ payload }) {
  const { shipmentKey, stopType, extra } = payload;

  yield put(shipmentsAction.setExtra({ stopType, extra, shipmentKey }));
  yield put(orderActions.fetchQuote());
}

function* handleTimePickers({ payload }) {
  const { stopType, timeParam, value, shipmentKey } = payload;

  const key = yield call(utils.getTimeKey, stopType, timeParam);
  const fieldKey = yield call(utils.getTimeKeyFull, shipmentKey, key);
  const input = yield call(dateAndTimeFormatters.formatRawTime, value);
  const autocompletedInput = yield call(
    dateAndTimeFormatters.autocompleteTimeInputIfCan,
    input
  );

  const { isValid, error } = yield call(
    dateAndTimeValidators.validateTime,
    autocompletedInput
  );

  yield all([
    put(warningActions.removeWarning([fieldKey])),
    put(errorActions.removeError([fieldKey])),
  ]);

  if (!isValid) {
    const originalTimes = yield select(
      shipmentsSelectors.getShipmentTimes,
      shipmentKey
    );
    const computedTimes = { ...originalTimes, [key]: input };

    if (error !== 'lengthError') {
      yield put(
        errorActions.setError({
          [fieldKey]: [...[error]],
        })
      );
    }

    yield put(
      shipmentsAction.changeValues({
        values: { ...computedTimes },
        shipmentKey,
      })
    );
  } else {
    const orderType = yield select(orderSelectors.getTypeOfOrder);
    yield put(actionsByType.shipments[orderType].changeTime({ ...payload }));
  }
}

/**
 * Not used for recurring
 */
function* setTimeToIfNeeded({ payload }) {
  const { stopType, timeParam, shipmentKey } = payload;
  if (
    stopType !== dateAndTimeConstants.STOP_TYPES.pickup ||
    timeParam !== dateAndTimeConstants.TIME_FIELD_PARAMS.from
  )
    return;
  const originalTimes = yield select(
    shipmentsSelectors.getShipmentTimes,
    shipmentKey
  );

  const { pickup_time_from, pickup_time_to } = originalTimes;
  const timeDifference = getHoursDifferenceByTimes(
    pickup_time_from,
    pickup_time_to
  );
  if (
    timeDifference >
    dateAndTimeConstants.CONSTANTS_FOR_RULES.DEFAULT_PICKUP_MARGIN_IN_HOURS
  ) {
    return;
  }

  const pickUpToSet = yield call(
    dateAndTimeMethods.getAutocompletedPickupToTimeAndDate,
    {
      originalTimes,
    }
  );
  const key = yield call(
    utils.getTimeKey,
    stopType,
    dateAndTimeConstants.TIME_FIELD_PARAMS.to
  );
  const fieldKey = yield call(utils.getTimeKeyFull, shipmentKey, key);

  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    {
      ...originalTimes,
      ...pickUpToSet,
    },
    shipmentKey
  );
  const dropoffTimeToKey = yield call(keyGenerator.shipmentData.time, {
    stopType: 'dropoff',
    timeParam: 'to',
  });

  yield put(warningActions.removeWarning([fieldKey]));
  yield put(
    shipmentsActions.changeValues({
      values: {
        ...pickUpToSet,
        ...(errors[dropoffTimeToKey] ? { [dropoffTimeToKey]: '' } : {}),
      },
      shipmentKey,
    })
  );

  if (!isEmpty(errors[dropoffTimeToKey])) {
    const fieldWarningKey = yield call(
      utils.getTimeKeyFull,
      shipmentKey,
      dropoffTimeToKey
    );
    yield put(warningActions.setWarning({ [fieldWarningKey]: 'warning' }));
  }
}

function* prepareForRecurring() {
  const pickUpDate = yield select(dorRangeSelectors.getRangeFrom);
  if (!pickUpDate) {
    return;
  }
  const addDropOffDays = yield select(dorDropOffSelectors.getAddDays);
  const dropOffDate = addDays(pickUpDate, addDropOffDays);

  yield put(
    shipmentsAction.changeValues({
      shipmentKey: 'FIRST_SHIPMENT_RECURRING',
      values: {
        pickup_date_from: pickUpDate,
        pickup_date_to: pickUpDate,
        dropoff_date_from: dropOffDate,
        dropoff_date_to: dropOffDate,
      },
    })
  );
}

function* handleSelectTimePickers({ payload }) {
  const { stopType, timeParam, value, shipmentKey } = payload;

  const key = yield call(utils.getTimeKey, stopType, timeParam);
  const fieldKey = yield call(utils.getTimeKeyFull, shipmentKey, key);
  const input = yield call(dateAndTimeFormatters.formatRawTime, value);
  const { isValid, error } = yield call(
    dateAndTimeValidators.validateTime,
    input
  );

  yield all([
    put(warningActions.removeWarning([fieldKey])),
    put(errorActions.removeError([fieldKey])),
  ]);

  if (!isValid) {
    const originalTimes = yield select(
      shipmentsSelectors.getShipmentTimes,
      shipmentKey
    );
    const computedTimes = { ...originalTimes, [key]: input };

    if (error !== 'lengthError') {
      yield put(
        errorActions.setError({
          [fieldKey]: [...[error]],
        })
      );
    }

    yield put(
      shipmentsAction.changeValues({
        values: { ...computedTimes },
        shipmentKey,
      })
    );
  } else {
    const orderType = yield select(orderSelectors.getTypeOfOrder);

    yield put(actionsByType.shipments[orderType].selectTime({ ...payload }));
  }
}

function* handleTimePickerBlur({ payload }) {
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  yield put(
    actionsByType.shipments[orderType].onBlurTimePicker({ ...payload })
  );
}

function* correctTimes({ shipmentKey }) {
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  return yield call(sagasByType.correctTimes[orderType], shipmentKey);
}

function* handleDatePickers({ payload }) {
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  yield put(actionsByType.shipments[orderType].changeDate({ ...payload }));
  yield call(prepareForRecurring);
}

function* calculateRoute({ stopType, address, shipmentKey }) {
  const pickupAddress =
    stopType === 'pickup'
      ? address
      : yield select(
          shipmentsSelectors.getShipmentAddress,
          shipmentKey,
          'pickup'
        );
  const dropoffAddress =
    stopType === 'dropoff'
      ? address
      : yield select(
          shipmentsSelectors.getShipmentAddress,
          shipmentKey,
          'dropoff'
        );
  if (isEmpty(pickupAddress) || isEmpty(dropoffAddress)) {
    return;
  }
  try {
    const params = {
      zip_from: pickupAddress.zipcode,
      country_from: pickupAddress.country,
      zip_to: dropoffAddress.zipcode,
      country_to: dropoffAddress.country,
    };
    const response = yield call(api.getRoutes, { params });
    const routes = response?.data?.results || [];
    yield put(
      shipmentsAction.changeValues({
        values: { route: routes[0] },
        shipmentKey,
      })
    );
  } catch (err) {
    console.error('Calculate route error', err);
  }
}

function* handleAddresses({ payload }) {
  const { shipmentKey, address, rawAddress, rawKey, key } = payload;
  yield put(
    shipmentsAction.changeValues({
      values: {
        [key]: address,
        [rawKey]: rawAddress,
      },
      shipmentKey,
    })
  );
}

function* handleSetDefaults({ payload }) {
  const {
    shipmentKey,
    forceSetDefaults,
    customKeys,
    orderType = yield select(orderSelectors.getTypeOfOrder),
  } = payload;

  const originalTimes = yield select(
    shipmentsSelectors.getShipmentTimes,
    shipmentKey
  );
  const isEditing = yield select(orderSelectors.getIsEditing);

  if (!isEditing) {
    yield put(
      shipmentsAction.cleanCommentsAndInternalReference({ ...payload })
    );
  }

  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    originalTimes,
    shipmentKey
  );

  const isEconomyModeEnabled = yield select(
    orderSelectors.getIsEconomyModeEnabled
  );
  const errorsFiltered = errorsMethods.filterTimeErrorsForEconomyMode(
    errors,
    isEconomyModeEnabled
  );

  const formTimesAreCompletedWithoutErrors =
    Object.keys(errorsFiltered).length < 1 &&
    !utils.keyFromObjectHasEmptyValue(originalTimes);

  if (formTimesAreCompletedWithoutErrors && !forceSetDefaults) {
    return;
  }

  const defaultsPerOrderType = {
    [orderConstants.DIRECT_ORDER]: () =>
      utils.getGenericDefaultDates(originalTimes),
    [orderConstants.DIRECT_ORDER_RECURRING]: () =>
      utils.getGenericDefaultDates(originalTimes),
    [orderConstants.COLLECTION]: () =>
      utils.getGenericDefaultDates(originalTimes),
    [orderConstants.DISTRIBUTION]: () =>
      utils.getGenericDefaultDates(originalTimes),
  };

  const defaultValues = defaultsPerOrderType[orderType]();
  const filteredValues = customKeys
    ? customKeys.reduce(
        (current, key) => ({ ...current, [key]: defaultValues[key] }),
        {}
      )
    : defaultValues;

  yield put(
    shipmentsAction.changeValues({
      values: { ...filteredValues },
      shipmentKey,
    })
  );
  yield call(handleGetMaximum);
  yield put(warningActions.cleanWarnings());
  yield put(orderActions.fetchQuote());
}

function* handleCreateShipment({ payload }) {
  const { shipmentKey } = payload;
  const orderType = yield select(orderSelectors.getTypeOfOrder);

  yield put(actionsByType.shipments[orderType].createShipment({ shipmentKey }));
  yield put(actionsByType.quote[orderType].cleanQuote());
  yield put(shipmentsAction.setDefaults({ shipmentKey }));
}

function* handleDeleteShipment({ payload }) {
  const { shipmentKey } = payload;
  const orderType = yield select(orderSelectors.getTypeOfOrder);

  const shipmentKeys = yield select(orderSelectors.getShipmentKeys);
  if (shipmentKeys.length >= 3) {
    yield put(
      actionsByType.shipments[orderType].removeShipment({ shipmentKey })
    );
    yield put(shipmentsAction.removeShipment({ shipmentKey }));
    yield call(isMaxReached);
    yield put(orderActions.fetchQuote());
  }
}

export default function* () {
  yield takeLatest(types.createShipment$, handleCreateShipment);
  yield takeLatest(types.deleteShipment$, handleDeleteShipment);
  yield takeLatest(types.changeExtras$, handleChangeExtras);

  // TimePickers
  yield takeLatest(types.changeTime$, handleTimePickers);
  yield takeLatest(types.selectTime$, handleSelectTimePickers);
  yield takeLatest(types.onBlurTimePicker$, handleTimePickerBlur);

  // DatePickers
  yield takeLatest(types.changeDate$, handleDatePickers);

  // Addresses
  yield takeLatest(types.changeAddress$, handleAddresses);

  // Defaults
  yield takeEvery(types.setDefaults$, handleSetDefaults);

  // Recurring
  yield takeLatest(types.prepareForRecurringQuote$, prepareForRecurring);
}

function* correctTimesByShipment(shipmentKey, errorsInOthers) {
  const times = yield select(shipmentsSelectors.getShipmentTimes, shipmentKey);
  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    times,
    shipmentKey
  );
  const errorsWithOtherErrors = {
    ...(errors || {}),
    ...(errorsInOthers || {}),
  };

  if (!isEmpty(errorsWithOtherErrors)) {
    const cleaned = Object.keys(errorsWithOtherErrors).reduce(
      (acc, errorKey) => ({
        ...acc,
        [errorKey]: '',
      }),
      {}
    );

    const warnings = Object.keys(errorsWithOtherErrors).reduce(
      (acc, errorKey) => ({
        ...acc,
        [keyGenerator.dateTime({
          shipmentKey,
          dateTimeKey: errorKey,
        })]: 'warning',
      }),

      {}
    );

    yield all([
      put(
        shipmentsActions.changeValues({ values: { ...cleaned }, shipmentKey })
      ),
      put(warningActions.setWarning(warnings)),
    ]);
  }
  return true;
}

export {
  calculateRoute,
  correctTimes,
  handleTimePickers,
  handleTimePickerBlur,
  handleDatePickers,
  handleSetDefaults,
  prepareForRecurring,
  setTimeToIfNeeded,
  correctTimesByShipment,
};
